import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from '@qodo/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { TriangleAlert, UserStarIcon } from 'lucide-react';
import { useLoginGitlabMTMutation } from 'network/mutations/useLoginGitlabMTMutation';
import { ROUTES } from 'routes/consts';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { prependHttpsIfNeeded } from '../../GithubManifestInstallationDialog/utils';
import {
  CONFIGURE_APP_FIELDS,
  CONFIGURE_MT_APP_FIELDS,
  FORM_IDS,
} from '../consts';
import { getConfigureMTAppFormSchema } from '../schemas';
import { ConfigureMTAppFormData } from '../types';

export const ConfigureMTApp = () => {
  const { setEarliestStepAvailable, setIsPending } = useMultiStepDialog();

  const methods = useForm<ConfigureMTAppFormData>({
    resolver: zodResolver(getConfigureMTAppFormSchema()),
    defaultValues: {
      [CONFIGURE_APP_FIELDS.GITLAB_URL]: '',
    },
  });

  const { mutate: loginGitlabMT, isPending: isLoginGitlabMTPending } =
    useLoginGitlabMTMutation();

  const spawnConfigure = spawn('dialogs.gitlab-installation.configure-app-mt');

  const formClassName = cn(
    "[&_[data-slot='form-label']]:font-mono",
    "[&_[data-slot='form-label']]:text-xs",
    "[&_[data-slot='form-label']]:leading-[140%]",
    "[&_[data-slot='form-description']]:text-text-bg-neutral-mild",
  );

  useEffect(() => {
    setEarliestStepAvailable(0);
  }, []);

  useEffect(() => {
    setIsPending(isLoginGitlabMTPending);
  }, [isLoginGitlabMTPending]);

  const onSubmit = (data: ConfigureMTAppFormData) => {
    setIsPending(true);
    const frontendRedirectUrl = `${window.location.origin}${ROUTES.GITLAB_MT_INSTALLATION_STEP_4}${location.search}`;
    const gitlabUrl = prependHttpsIfNeeded(data.gitlabUrl);

    loginGitlabMT(
      { ...data, gitlabUrl, frontendRedirectUrl },
      {
        onSuccess: (response) => {
          window.location.href = response.authorizationUrl;
        },
        onError: () => {
          setIsPending(false);
        },
      },
    );
  };

  return (
    <Step
      title={spawnConfigure('title')}
      description={spawnConfigure('description')}
    >
      <div className="gap-4 items-center flex">
        <UserStarIcon className="size-4" />
        <span className="text-sm w-fit">
          {spawnConfigure('service-account-role-requirement')}
        </span>
      </div>
      <div className="gap-4 items-center flex">
        <TriangleAlert className="size-4" />
        <span className="text-sm w-fit">
          {spawnConfigure('service-account-login-requirement')}
        </span>
      </div>
      <hr className="bg-fill-table-neutral-active-lvl-2 h-px border-0" />
      <Form
        methods={methods}
        onSubmit={onSubmit}
        className={formClassName}
        {...({ id: FORM_IDS.CONFIGURE_MT_APP } as Record<string, string>)}
      >
        <Form.Input
          name={CONFIGURE_MT_APP_FIELDS.GITLAB_URL}
          label={spawnConfigure('gitlab-url.label')}
          placeholder={spawnConfigure('gitlab-url.placeholder')}
        />
      </Form>
    </Step>
  );
};
